import { NavLink, useNavigate, useLocation } from 'react-router-dom';
import { LayoutDashboard, Heart, Users, Settings, LogOut, Menu, X, Newspaper, BookOpen, CalendarHeart, Radio, Quote, Image, Award, ChevronDown, ChevronRight, Globe, Package, FileText, MessageSquareText, MessageSquare, Sparkles } from 'lucide-react';
import { useState } from 'react';

interface NavItem {
  to: string;
  label: string;
  icon: React.ElementType;
}

interface NavGroup {
  label: string;
  icon: React.ElementType;
  children: NavItem[];
}

type NavEntry = NavItem | NavGroup;

const navEntries: NavEntry[] = [
  { to: '/admin/dashboard', label: 'Dashboard', icon: LayoutDashboard },
  { to: '/admin/donations', label: '捐款管理', icon: Heart },
  { to: '/admin/donors', label: '捐贈者', icon: Users },
  { to: '/admin/monthly-pledges', label: '月捐計劃', icon: CalendarHeart },
  { to: '/admin/broadcast-tasks', label: '廣播訊息', icon: Radio },
  { to: '/admin/system-message-templates', label: '訊息模板', icon: MessageSquareText },
  { to: '/admin/message-logs', label: '訊息記錄', icon: MessageSquare },
  { to: '/admin/events', label: '活動管理', icon: CalendarHeart },
  { to: '/admin/receipt-requests', label: '收據申請', icon: FileText },
  {
    label: '網頁內容',
    icon: Globe,
    children: [
      { to: '/admin/testimonials', label: '權威見證', icon: Award },
      { to: '/admin/media-coverage', label: '媒體報導', icon: Newspaper },
      { to: '/admin/stories', label: '學生故事', icon: BookOpen },
      { to: '/admin/media-library', label: '媒體庫', icon: Image },
    ],
  },
  {
    label: '阿占空間',
    icon: Quote,
    children: [
      { to: '/admin/ahjim-upsell', label: '阿占Upsell', icon: Sparkles },
      { to: '/admin/ahjim-quotes', label: '阿占語錄', icon: Quote },
      { to: '/admin/ahjim-illustrations', label: '阿占插圖', icon: Image },
      { to: '/admin/phone-case-orders', label: '手機殼訂單', icon: Package },
    ],
  },
  { to: '/admin/settings', label: '系統設置', icon: Settings },
];

function isNavGroup(entry: NavEntry): entry is NavGroup {
  return 'children' in entry;
}

export default function AdminLayout({ children }: { children: React.ReactNode }) {
  const navigate = useNavigate();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(() => {
    const s = new Set<string>();
    const paths: Record<string, string[]> = {
      '網頁內容': ['/admin/testimonials', '/admin/media-coverage', '/admin/stories', '/admin/media-library'],
      '阿占空間': ['/admin/ahjim-upsell', '/admin/ahjim-quotes', '/admin/ahjim-illustrations', '/admin/phone-case-orders'],
    };
    for (const [label, prefixes] of Object.entries(paths)) {
      if (prefixes.some((p) => location.pathname.startsWith(p))) {
        s.add(label);
      }
    }
    return s;
  });

  const handleLogout = () => {
    localStorage.removeItem('admin_token');
    localStorage.removeItem('admin_user');
    navigate('/admin/login');
  };

  const adminName = (() => {
    try {
      const raw = localStorage.getItem('admin_user');
      if (raw) {
        const name = JSON.parse(raw).name || '管理員';
        // 檢測亂碼（替換字符或連續問號）
        if (name.includes('\uFFFD') || /\?{2,}/.test(name)) {
          return '管理員';
        }
        return name;
      }
    } catch { /* ignore */ }
    return '管理員';
  })();

  return (
    <div className="min-h-screen bg-cream flex">
      {/* Mobile overlay */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black/40 z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside
        className={`
          fixed lg:static inset-y-0 left-0 z-50 w-64 bg-white border-r border-brand-100
          transform transition-transform duration-200 lg:transform-none
          ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}
          flex flex-col
        `}
      >
        <div className="h-16 flex items-center justify-between px-4 border-b border-brand-100">
          <span className="font-bold text-brand-800">好餸傳愛後台</span>
          <button className="lg:hidden text-gray-500" onClick={() => setSidebarOpen(false)}>
            <X className="w-5 h-5" />
          </button>
        </div>
        <nav className="flex-1 p-4 space-y-1">
          {navEntries.map((entry, idx) => {
            if (isNavGroup(entry)) {
              const isChildActive = entry.children.some((c) => location.pathname.startsWith(c.to));
              const isExpanded = expandedGroups.has(entry.label);
              return (
                <div key={idx}>
                  <button
                    onClick={() => setExpandedGroups((prev) => {
                      const next = new Set(prev);
                      if (next.has(entry.label)) next.delete(entry.label);
                      else next.add(entry.label);
                      return next;
                    })}
                    className={`flex items-center justify-between w-full gap-3 px-4 py-3 rounded-xl text-sm font-medium transition-colors ${
                      isChildActive
                        ? 'bg-brand-50 text-brand-700'
                        : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                    }`}
                  >
                    <span className="flex items-center gap-3">
                      <entry.icon className="w-5 h-5" />
                      {entry.label}
                    </span>
                    {isExpanded ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                  </button>
                  {isExpanded && (
                    <div className="ml-4 mt-1 space-y-1">
                      {entry.children.map((child) => (
                        <NavLink
                          key={child.to}
                          to={child.to}
                          className={({ isActive }) =>
                            `flex items-center gap-3 px-4 py-2.5 rounded-xl text-sm font-medium transition-colors ${
                              isActive
                                ? 'bg-brand-50 text-brand-700'
                                : 'text-gray-500 hover:bg-gray-50 hover:text-gray-700'
                            }`
                          }
                          onClick={() => setSidebarOpen(false)}
                        >
                          <child.icon className="w-4 h-4" />
                          {child.label}
                        </NavLink>
                      ))}
                    </div>
                  )}
                </div>
              );
            }
            return (
              <NavLink
                key={entry.to}
                to={entry.to}
                className={({ isActive }) =>
                  `flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-medium transition-colors ${
                    isActive
                      ? 'bg-brand-50 text-brand-700'
                      : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                  }`
                }
                onClick={() => setSidebarOpen(false)}
              >
                <entry.icon className="w-5 h-5" />
                {entry.label}
              </NavLink>
            );
          })}
        </nav>
        <div className="p-4 border-t border-brand-100">
          <button
            onClick={handleLogout}
            className="flex items-center gap-3 px-4 py-3 w-full rounded-xl text-sm font-medium text-red-600 hover:bg-red-50 transition-colors"
          >
            <LogOut className="w-5 h-5" />
            登出
          </button>
        </div>
      </aside>

      {/* Main content */}
      <div className="flex-1 flex flex-col min-w-0">
        {/* Top bar */}
        <header className="h-16 bg-white border-b border-brand-100 flex items-center justify-between px-4 lg:px-6">
          <button
            className="lg:hidden text-gray-600"
            onClick={() => setSidebarOpen(true)}
          >
            <Menu className="w-6 h-6" />
          </button>
          <div className="flex items-center gap-2 ml-auto">
            <div className="w-8 h-8 rounded-full bg-brand-100 flex items-center justify-center">
              <Users className="w-4 h-4 text-brand-600" />
            </div>
            <span className="text-sm font-medium text-gray-700">{adminName}</span>
          </div>
        </header>

        {/* Page content */}
        <main className="flex-1 p-4 lg:p-6 overflow-auto">
          {children}
        </main>
      </div>
    </div>
  );
}
